import React from 'react';
import { TbArrowsDoubleNeSw, TbBolt, TbMathMaxMin, TbMessage } from 'react-icons/tb';

import { Popover } from 'driver.js';

import { PermissionDiagram, PermissionDiagramGeneral } from 'models';

import { ExpertSupportTourStep } from 'services/drivers/ExpertSupportDriverService';
import { FaultSimTourStep } from 'services/drivers/FaultSimDriverService';
import { PowerFlowSimTourStep } from 'services/drivers/PowerFlowSimDriverService';
import { WireSizingTourStep } from 'services/drivers/WireSizingDriverService';

export enum FlowType {
    EXPERT_SUPPORT = 'expert-support',
    FAULT_SIMULATIONS = 'fault-simulations',
    POWER_FLOW_SIMULATIONS = 'power-flow-simulations',
    WIRE_SIZING = 'wire-sizing',
}

export type FlowStep = {
    selector: string | null;
    popover: Popover;
    popoverOffset?: number;
    isLastStep?: boolean;
    delay?: number;
    showNextButton?: boolean;
};

export type FlowConfig = {
    title: string;
    description: string;
    icon: React.ReactNode;
    tourKey: FlowType;
    color: string;
};

export const FLOWS: Record<FlowType, FlowConfig> = {
    [FlowType.EXPERT_SUPPORT]: {
        title: 'DC Expert Support',
        description:
            'Ask a DC expert directly in your project. Get answers from engineers who have designed, simulated, and commissioned real DC power systems.',
        icon: React.createElement(TbMessage, { strokeWidth: 1.25 }),
        tourKey: FlowType.EXPERT_SUPPORT,
        color: 'blue',
    },

    [FlowType.POWER_FLOW_SIMULATIONS]: {
        title: 'Power Flow Simulation',
        description: 'Simulate how your system behaves under different conditions and optimize your design.',
        icon: React.createElement(TbArrowsDoubleNeSw, { strokeWidth: 1.25 }),
        tourKey: FlowType.POWER_FLOW_SIMULATIONS,
        color: 'green',
    },
    [FlowType.WIRE_SIZING]: {
        title: 'Wire Sizing',
        description:
            'Let our wire sizing engine suggest or validate your wire sizes according to IEC and NEC standards.',
        icon: React.createElement(TbMathMaxMin, { strokeWidth: 1.25 }),
        tourKey: FlowType.WIRE_SIZING,
        color: 'red',
    },
    [FlowType.FAULT_SIMULATIONS]: {
        title: 'Fault Simulation',
        description: 'Run short-circuit scenarios across your DC system and find the perfect protection devices.',
        icon: React.createElement(TbBolt, { strokeWidth: 1.25 }),
        tourKey: FlowType.FAULT_SIMULATIONS,
        color: 'yellow',
    },
};

export const FLOW_KEYS = Object.keys(FLOWS) as FlowType[];

export const FLOW_REQUIRED_PERMISSION: Partial<Record<FlowType, PermissionDiagram>> = {
    [FlowType.EXPERT_SUPPORT]: PermissionDiagramGeneral.REQUEST_SUPPORT,
};

export const isValidFlow = (flow: string | undefined): flow is FlowType => {
    return !!flow && FLOW_KEYS.includes(flow as FlowType);
};

export type TourStep = ExpertSupportTourStep | FaultSimTourStep | PowerFlowSimTourStep | WireSizingTourStep;
